<template>
    <div class="vuex-number-input" :class="cssClass">
        <p v-if="label" class="rtls-text-field-container__label">
            {{ toSentenceCase($t(label)) }}
        </p>
        <v-text-field
            ref="textField"
            v-model="formattedNumber"
            :disabled="isDisabled"
            reverse
            :class="`rtls-text-field rtls-text-field--${theme.name}`"
            :placeholder="placeholder"
            :hide-details="hideDetails"
            :rules="rules"
            :height="height"
            :name="name"
            autocomplete="off"
            validate-on="blur"
            :bg-color="theme.bgColor"
            @keypress="
                params =>
                    positiveOnly
                        ? validatePositiveIntegerInput(params)
                        : validateIntegerInput(params)
            "
            @paste="validateNumberOnPaste"
            @change="updateModel"
        />
    </div>
</template>

<script>
import { isNil } from 'lodash';
import numeral from 'numeral';
import vuexComponentMixin from '../../mixins/vuex-component';
import useForceValidateOnMount from '@/js/composables/useForceValidateOnMount';
import validateNumberInputMixin from '../../mixins/validate-number-input';
import readonly from '@/js/mixins/readonly';
import * as generalFilters from '@/js/filters/general-filters';

const defaultFormat = 'defaultNumber';

export default {
    mixins: [vuexComponentMixin, validateNumberInputMixin, readonly],
    props: {
        disabled: Boolean,
        change: {
            type: Function,
            default: () => {},
        },
        name: {
            type: String,
            default: null,
        },
        theme: {
            type: Object,
            default: () => ({
                name: 'grey',
                bgColor: null,
            }),
        },
        format: {
            type: String,
            default: defaultFormat,
        },
        placeholder: {
            type: String,
            default: '—',
        },
        saveAsString: {
            type: Boolean,
            default: () => false,
        },
        positiveOnly: {
            type: Boolean,
            default: false,
        },
        hideDetails: {
            type: [Boolean, String],
            default: null,
        },
        forceValidateOnMount: {
            type: Boolean,
            default: false,
        },
        cssClass: String,
        height: String,
    },
    emits: ['change'],

    setup() {
        useForceValidateOnMount({ refName: 'textField' });
    },
    data() {
        return {
            numberInput: null,
        };
    },

    computed: {
        formattedNumber: {
            get() {
                const value = !this.saveAsString
                    ? this.formattedValue(this.format, this.model)
                    : this.model;
                return isNil(this.model) || value === '-' ? null : value;
            },
            set(newValue) {
                // Format the inputted number. This applies any rounding required for the
                // number formatting. E.g. 10.6666 with format '0.0' will convert to 10.7
                const formattedInput = this.formattedValue(this.format, newValue);

                this.numberInput = this.saveAsString
                    ? formattedInput
                    : numeral(formattedInput).value();
            },
        },

        isDisabled() {
            return this.disabled || this.isReadOnly;
        },
    },

    created() {
        this.numberInput = this.model;
    },

    methods: {
        updateModel() {
            this.model = this.numberInput;
            this.$emit('change', this.numberInput);
        },
        formattedValue(filterName, data) {
            const filter = generalFilters[filterName];
            if (typeof filter === 'function') {
                return filter(data);
            }
            return this.$n(filterName, data);
        },
    },
};
</script>
